import { useState, useEffect } from "react";
import { Check, Clock, MessageSquare, X, Plus, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import TaskCreationDialog from "./TaskCreationDialog";
import type { Todo } from "@/types/database";

interface TodoManagementProps {
  customers?: Array<{ id: string; name: string; }>;
  showCreateForm?: boolean;
  preselectedCustomer?: { id: string; name: string; };
  showFloatingAddButton?: boolean;
  onTaskCountChange?: (count: number) => void;
}

export const TodoManagement = ({ customers = [], showCreateForm = true, preselectedCustomer, showFloatingAddButton = false, onTaskCountChange }: TodoManagementProps) => {
  const { toast } = useToast();
  const [todos, setTodos] = useState<Todo[]>([]);
  const [completingTodo, setCompletingTodo] = useState<Todo | null>(null);
  const [selectedTodo, setSelectedTodo] = useState<Todo | null>(null);
  const [completionComment, setCompletionComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadTodos();
  }, [preselectedCustomer]);

  const loadTodos = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setLoading(false);
        return;
      }

      let query = supabase
        .from('todos')
        .select('*');

      // Filter by customer if preselected
      if (preselectedCustomer) {
        query = query.eq('customer_id', preselectedCustomer.id);
      }

      const { data, error } = await query
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading todos:', error);
        toast({
          title: "Fehler",
          description: "To-Dos konnten nicht geladen werden.",
          variant: "destructive",
        });
      } else {
        setTodos(data || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateDueDate = (reminderDays: number): string => {
    const date = new Date();
    date.setDate(date.getDate() + reminderDays);
    return date.toISOString().split('T')[0];
  };

  // Handler for default task selection
  const handleDefaultTaskSelect = (defaultTask: any) => {
    if (!preselectedCustomer) {
      toast({
        title: "Fehler",
        description: "Kein Kunde ausgewählt.",
        variant: "destructive",
      });
      return;
    }

    // Create todo from default task
    createTodoFromDefaultTask(defaultTask);
  };

  const createCustomTodo = async (title: string, description: string, dueDate: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast({
        title: "Fehler",
        description: "Sie müssen angemeldet sein, um To-Dos zu erstellen.",
        variant: "destructive",
      });
      return;
    }

    if (!preselectedCustomer) {
      toast({
        title: "Fehler",
        description: "Kein Kunde ausgewählt.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data, error } = await supabase
        .from('todos')
        .insert({
          customer_id: preselectedCustomer.id,
          title: title,
          description,
          due_date: dueDate,
          user_id: session.user.id
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating custom todo:', error);
        toast({
          title: "Fehler",
          description: "To-Do konnte nicht erstellt werden.",
          variant: "destructive",
        });
        return;
      }

      setTodos(prev => [data, ...prev]);

      // Send email notification to customer
      try {
        await supabase.functions.invoke('send-task-notification', {
          body: { todoId: data.id, type: 'created' }
        });
      } catch (emailError) {
        console.error('Error sending task notification email:', emailError);
      }

      toast({
        title: "To-Do erstellt",
        description: `${data.title} wurde zur Liste hinzugefügt.`,
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive",
      });
    }
  };

  const createTodoFromDefaultTask = async (defaultTask: any) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast({
        title: "Fehler",
        description: "Sie müssen angemeldet sein, um To-Dos zu erstellen.",
        variant: "destructive",
      });
      return;
    }

    if (!preselectedCustomer) {
      toast({
        title: "Fehler",
        description: "Kein Kunde ausgewählt.",
        variant: "destructive",
      });
      return;
    }

    const title = defaultTask.name;
    const description = defaultTask.description || defaultTask.name;
    const reminderDays = defaultTask.due_in_days || 7;

    try {
      const { data, error } = await supabase
        .from('todos')
        .insert({
          customer_id: preselectedCustomer.id,
          title,
          description,
          due_date: calculateDueDate(reminderDays),
          user_id: session.user.id
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating todo from default task:', error);
        toast({
          title: "Fehler",
          description: "To-Do konnte nicht erstellt werden.",
          variant: "destructive",
        });
        return;
      }

      setTodos(prev => [data, ...prev]);

      // Send email notification to customer
      try {
        await supabase.functions.invoke('send-task-notification', {
          body: { todoId: data.id, type: 'created' }
        });
      } catch (emailError) {
        console.error('Error sending task notification email:', emailError);
      }

      toast({
        title: "To-Do erstellt",
        description: `${data.title} wurde zur Liste hinzugefügt.`,
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive",
      });
    }
  };

  const completeTodo = async (todoId: string, comment: string) => {
    try {
      const { error } = await supabase
        .from('todos')
        .update({
          completed: true,
          completed_at: new Date().toISOString(),
          completed_comment: comment
        })
        .eq('id', todoId);

      if (error) {
        console.error('Error completing todo:', error);
        toast({
          title: "Fehler",
          description: "To-Do konnte nicht abgeschlossen werden.",
          variant: "destructive",
        });
        return;
      }

      setTodos(prev => prev.map(todo => 
        todo.id === todoId 
          ? { 
              ...todo, 
              completed: true, 
              completed_at: new Date().toISOString(),
              completed_comment: comment 
            }
          : todo
      ));

      // Send email notification to customer
      try {
        await supabase.functions.invoke('send-task-notification', {
          body: { todoId, type: 'completed' }
        });
      } catch (emailError) {
        console.error('Error sending task notification email:', emailError);
      }

      setCompletingTodo(null);
      setCompletionComment("");

      toast({
        title: "To-Do abgeschlossen",
        description: "Der Vermerk wurde zum Kundenprotokoll hinzugefügt.",
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive",
      });
    }
  };

  const deleteTodo = async (todoId: string) => {
    try {
      const { error } = await supabase
        .from('todos')
        .delete()
        .eq('id', todoId);

      if (error) {
        console.error('Error deleting todo:', error);
        toast({
          title: "Fehler",
          description: "To-Do konnte nicht gelöscht werden.",
          variant: "destructive",
        });
        return;
      }

      setTodos(prev => prev.filter(todo => todo.id !== todoId));
      toast({
        title: "To-Do gelöscht",
        description: "Das To-Do wurde entfernt.",
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive",
      });
    }
  };

  const getTypeLabel = (type: Todo['type']) => {
    return type;
  };

  const getTypeColor = (type: Todo['type']) => {
    return "outline";
  };

  const getDaysUntilDue = (dueDate: string) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getPriorityColor = (daysUntil: number): "destructive" | "outline" | "secondary" => {
    if (daysUntil < 0) return "destructive";
    if (daysUntil <= 3) return "secondary";
    return "outline";
  };

  const getCustomerName = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'Unbekannter Kunde';
  };

  const activeTodos = todos.filter(todo => !todo.completed);
  const completedTodos = todos.filter(todo => todo.completed);

  // Notify parent about task count changes
  useEffect(() => {
    if (onTaskCountChange) {
      onTaskCountChange(activeTodos.length);
    }
  }, [activeTodos.length, onTaskCountChange]);

  return (
    <div className="space-y-4 md:space-y-6">
      
      {/* Create Todo Button - Always visible at top */}
      <Card className="relative">
        <CardContent className="text-center py-6">
          <h3 className="text-lg font-semibold mb-2">To-Do Management</h3>
          <div className="flex justify-center">
            <Dialog open={isCreating} onOpenChange={setIsCreating}>
              <DialogTrigger asChild>
                <Button onClick={() => setIsCreating(true)} className="gap-1 px-3 py-1" size="sm">
                  <Plus className="h-3 w-3" />
                  <span className="text-xs">Neues To-Do erstellen</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-2xl max-h-[80vh] overflow-hidden">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Plus className="h-5 w-5" />
                    To-Do erstellen
                  </DialogTitle>
                  <DialogDescription>
                    {preselectedCustomer ? 
                      `Wählen Sie eine Standard-Aufgabe oder erstellen Sie eine eigene für ${preselectedCustomer.name}` :
                      "Erstellen Sie ein neues To-Do"
                    }
                  </DialogDescription>
                </DialogHeader>
                
                <TaskCreationDialog 
                  onTaskSelect={handleDefaultTaskSelect}
                  customerId={preselectedCustomer?.id}
                  customerName={preselectedCustomer?.name}
                  onCustomTaskCreate={createCustomTodo}
                />
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>
      

      {/* Complete Todo Dialog */}
      <Dialog open={!!completingTodo} onOpenChange={() => setCompletingTodo(null)}>
        <DialogContent className="mx-4 max-w-md md:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg">To-Do abschließen</DialogTitle>
            <DialogDescription className="text-sm">
              Fügen Sie einen Kommentar hinzu, der zum Kundenprotokoll hinzugefügt wird.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 md:space-y-4">
            <div className="space-y-2">
              <Label>Kommentar</Label>
              <Textarea
                value={completionComment}
                onChange={(e) => setCompletionComment(e.target.value)}
                placeholder="Was wurde erledigt? Zusätzliche Notizen..."
                rows={3}
              />
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2">
              <Button variant="outline" onClick={() => setCompletingTodo(null)} size="sm">
                Abbrechen
              </Button>
              <Button 
                onClick={() => completingTodo && completeTodo(completingTodo.id, completionComment)}
                size="sm"
              >
                <Check className="h-4 w-4 mr-2" />
                Abschließen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Active Todos */}
      {activeTodos.length > 0 && (
        <Card className="relative">
          <CardHeader className="px-4 md:px-6 pb-3">
            <CardTitle className="flex items-center justify-between text-lg">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 md:h-5 md:w-5" />
                Offene To-Dos ({activeTodos.length})
              </div>
            </CardTitle>
            <CardDescription className="text-sm">
              {preselectedCustomer ? 
                `anstehende Aufgaben für ${preselectedCustomer.name}` :
                "Ihre anstehenden Aufgaben und Termine"
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 md:px-6">
            <div className="space-y-2 md:space-y-3">
              {activeTodos.map((todo) => {
                const daysUntil = getDaysUntilDue(todo.due_date);
                const isOverdue = daysUntil < 0;
                const isDueSoon = daysUntil <= 3 && daysUntil >= 0;

                return (
                  <div key={todo.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setSelectedTodo(todo)}>
                     <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h4 className="font-medium text-sm md:text-base truncate">
                          {todo.title}{isOverdue ? " !" : ""}
                        </h4>
                        {isOverdue && (
                          <Badge variant="destructive" className="text-xs animate-pulse">Überfällig !</Badge>
                        )}
                        {isDueSoon && !isOverdue && (
                          <Badge variant="secondary" className="text-xs">Fällig bald</Badge>
                        )}
                      </div>
                       <p className="text-xs md:text-sm text-muted-foreground truncate max-w-full">
                        {todo.description} • Fällig: {new Date(todo.due_date).toLocaleDateString("de-DE")}
                        {isOverdue && ` (${Math.abs(daysUntil)} Tage überfällig)`}
                        {!isOverdue && daysUntil > 0 && ` (in ${daysUntil} Tagen)`}
                        {daysUntil === 0 && " (heute fällig)"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCompletingTodo(todo)}
                        className="h-8 w-8 p-0"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteTodo(todo.id)}
                        className="h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Completed Todos */}
      {completedTodos.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Check className="h-5 w-5" />
              Erledigte To-Dos ({completedTodos.length})
            </CardTitle>
            <CardDescription>
              Ihre abgeschlossenen Aufgaben
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {completedTodos.slice(0, 5).map((todo) => (
                <div key={todo.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium line-through break-words">{todo.title}</h4>
                      <Badge variant="outline" className="flex-shrink-0 text-xs px-1 py-0 h-4 hover:border-green-500">Erledigt</Badge>
                    </div>
                     <p className="text-xs text-muted-foreground">
                      Abgeschlossen: {todo.completed_at && new Date(todo.completed_at).toLocaleDateString('de-DE')}
                      {todo.completed_comment && ` • ${todo.completed_comment}`}
                    </p>
                  </div>
                </div>
              ))}
              {completedTodos.length > 5 && (
                <p className="text-sm text-muted-foreground text-center py-2">
                  ... und {completedTodos.length - 5} weitere erledigte To-Dos
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Todo Detail Dialog */}
      <Dialog open={!!selectedTodo} onOpenChange={(open) => !open && setSelectedTodo(null)}>
        <DialogContent className="mx-2 w-[95vw] max-w-[400px] md:mx-4 md:max-w-2xl overflow-hidden">
          {selectedTodo && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-base">
                  <CalendarClock className="h-5 w-5 flex-shrink-0" />
                  <span className="truncate">Aufgabe Details</span>
                </DialogTitle>
                <DialogDescription className="text-sm">
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-medium">Aufgabe</Label>
                    <p className="text-sm text-muted-foreground break-words">{selectedTodo.title.includes(':') ? selectedTodo.title.split(':').slice(1).join(':').trim() : selectedTodo.title}</p>
                  </div>

                  {selectedTodo.description && (
                    <div>
                      <Label className="text-sm font-medium">Notiz</Label>
                      <div className="text-sm text-muted-foreground break-words" 
                           dangerouslySetInnerHTML={{
                             __html: selectedTodo.description.replace(
                               /(https?:\/\/[^\s]+)/g, 
                               '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-primary hover:text-primary/80 underline">$1</a>'
                             )
                           }} 
                      />
                    </div>
                  )}

                  {selectedTodo.customer_id && (
                    <div>
                      <Label className="text-sm font-medium">Kunde</Label>
                      <p className="text-sm text-muted-foreground break-words">{getCustomerName(selectedTodo.customer_id)}</p>
                    </div>
                  )}

                  {selectedTodo.due_date && (
                    <div>
                      <Label className="text-sm font-medium">Fälligkeitsdatum</Label>
                      <div className="flex items-center gap-2 flex-wrap">
                        <CalendarClock className="h-4 w-4 flex-shrink-0" />
                        <p className="text-sm text-muted-foreground">
                          {new Date(selectedTodo.due_date).toLocaleDateString('de-DE')}
                        </p>
                        <Badge 
                          variant={getPriorityColor(getDaysUntilDue(selectedTodo.due_date))} 
                          className={`text-[10px] font-normal text-center px-1.5 py-0.5 flex-shrink-0 ${getDaysUntilDue(selectedTodo.due_date) < 0 ? 'animate-pulse' : ''}`}
                        >
                          {(() => {
                            const daysUntil = getDaysUntilDue(selectedTodo.due_date);
                            return daysUntil < 0 ? `${Math.abs(daysUntil)} Tage überfällig` : 
                                   daysUntil === 0 ? 'Heute' :
                                   daysUntil === 1 ? 'Morgen' :
                                   `${daysUntil} Tage`;
                          })()}
                        </Badge>
                      </div>
                    </div>
                   )}

                  <div>
                    <Label className="text-sm font-medium">Erstellt am</Label>
                    <p className="text-sm text-muted-foreground">
                      {new Date(selectedTodo.created_at).toLocaleDateString('de-DE', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>

                {!selectedTodo.completed && (
                  <>
                    <div className="border-t pt-4 space-y-3">
                      <Label className="text-sm font-medium">Abschluss-Kommentar (optional)</Label>
                      <Textarea
                        value={completionComment}
                        onChange={(e) => setCompletionComment(e.target.value)}
                        placeholder="Notizen zum Abschluss der Aufgabe..."
                        rows={3}
                      />
                    </div>

                    <div className="flex flex-col sm:flex-row justify-end gap-2">
                      <Button variant="outline" onClick={() => setSelectedTodo(null)} size="sm" className="h-8 px-2 text-xs">
                        Schließen
                      </Button>
                      <Button 
                        onClick={() => {
                          completeTodo(selectedTodo.id, completionComment);
                          setSelectedTodo(null);
                          setCompletionComment("");
                        }}
                        size="sm"
                        className="gap-1 h-8 px-2 text-xs"
                      >
                        <Check className="h-3 w-3" />
                        Aufgabe abschließen
                      </Button>
                    </div>
                  </>
                )}

                {selectedTodo.completed && (
                  <div className="flex justify-end">
                    <Button variant="outline" onClick={() => setSelectedTodo(null)} size="sm" className="h-8 px-2 text-xs">
                      Schließen
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};